import React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Theme } from '@mui/material';

const useStyles = createUseStyles((theme: Theme) => {
    const { color, radius, shadow, mq } = theme.custom;
    return {
        root: {
            backgroundColor: color.surface,
            border: `1px solid ${color.border}`,
            borderRadius: radius.lg,
            boxShadow: shadow.sm,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            padding: '14px 20px',
            borderBottom: `1px solid ${color.border}`,
            [mq.sm]: { padding: '12px 16px' },
        },
        headerNoBody: { borderBottom: 'none' },
        titleBlock: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, minWidth: 0 },
        title: { margin: 0, fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: color.text.primary },
        subtitle: { fontSize: 13, color: color.text.secondary },
        actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' },
        body: { minWidth: 0 },
        'pad-md': { padding: 20, [mq.sm]: { padding: 16 } },
        'pad-sm': { padding: 12 },
        'pad-none': { padding: 0 },
        footer: {
            padding: '12px 20px',
            borderTop: `1px solid ${color.border}`,
            backgroundColor: color.surfaceMuted,
            [mq.sm]: { padding: '12px 16px' },
        },
    };
});

export interface PanelProps {
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Chips next to the title. */
    badges?: React.ReactNode;
    actions?: React.ReactNode;
    padding?: 'md' | 'sm' | 'none';
    footer?: React.ReactNode;
    className?: string;
    bodyClassName?: string;
    children?: React.ReactNode;
    /** Heading level of the title; `h2` for page sections. */
    titleAs?: 'h2' | 'h3';
    id?: string;
}

/** A card that sits on the page canvas: optional title row, body, optional muted footer. Never nest a Panel inside a Panel. */
const Panel = ({ title, subtitle, badges, actions, padding = 'md', footer, className, bodyClassName, children, titleAs = 'h2', id }: PanelProps) => {
    const classes = useStyles();
    const TitleTag = titleAs;
    const headingId = React.useId();
    const hasHeader = Boolean(title || actions || badges);
    const hasBody = React.Children.toArray(children).length > 0;
    return (
        <section className={classNames(classes.root, className)} id={id} aria-labelledby={title ? headingId : undefined}>
            {hasHeader && (
                <div className={classNames(classes.header, { [classes.headerNoBody]: !hasBody && !footer })}>
                    <div className={classes.titleBlock}>
                        {title && <TitleTag className={classes.title} id={headingId}>{title}</TitleTag>}
                        {badges}
                        {subtitle && <span className={classes.subtitle}>{subtitle}</span>}
                    </div>
                    {actions && <div className={classes.actions}>{actions}</div>}
                </div>
            )}
            {hasBody && (
                <div className={classNames(classes.body, classes[`pad-${padding}`], bodyClassName)}>{children}</div>
            )}
            {footer && <div className={classes.footer}>{footer}</div>}
        </section>
    );
};

export default Panel;
